import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search, Eye, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

function Patients({ toggleSidebar }) {
  const [responseData, setResponseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showEntries, setShowEntries] = useState("10");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);

  const navigate = useNavigate();

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid or empty roles data received");
      }

      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response?.data
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Patients page
  const hasPermission = (permission) => {
    const patientsPrivileges = userPrivileges["Patients"];
    if (!patientsPrivileges) {
      console.log("No privileges found for Patients page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Patients:`,
      patientsPrivileges
    );
    if (patientsPrivileges.all) return true;
    const hasPerm = patientsPrivileges[permission] || false;
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  // Fetch patient data
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Patients page");
      setError("You do not have permission to view patients.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const userType = localStorage.getItem("user_type") || "unknown";
      let patients = [];
      if (userType === "Doctor") {
        const username = localStorage.getItem("username") || "";
        if (!username) {
          throw new Error("Doctor username not found in localStorage");
        }
        // Fetch doctor by username (doctor_id) to get _id
        const doctorResponse = await axios.get(
          `${Apiurl}/doctor_by_doctor_id/${username}`
        );
        const doctorId = doctorResponse.data?._id;
        if (!doctorId) {
          throw new Error("Doctor ID not found");
        }
        // Fetch patients for this doctor using the doctor_patients endpoint
        const patientsResponse = await axios.get(
          `${Apiurl}/doctor_patients/${doctorId}`
        );
        patients = patientsResponse.data || [];
      } else {
        // For staff or others, fetch all patients
        const response = await axios.get(`${Apiurl}/patient_details`);
        patients = response.data || [];
      }
      console.log("Fetched patient data:", patients);
      setResponseData(patients.reverse());
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching patient data:",
        error.message,
        error.response?.data
      );
      setError("Failed to fetch patient data. Please try again.");
      setTimeout(() => setError(null), 3000);
      setResponseData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("Fetching initial data...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (hasPermission("read")) {
      console.log("Fetching patient data due to read permission");
      fetchData();
    } else if (!loadingPrivileges) {
      console.warn("Read permission denied for Patients page");
      setError("You do not have permission to view patients.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.patient_id,
        item.registration_date,
        item.first_name,
        item.gender,
        item.age,
        item.mobile_no,
        item.patient_type,
      ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    console.log("Filtered data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Reset to first page when search term or items per page changes
  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm or itemsPerPage change"
    );
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  const handleAddPurchaseClick = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Patients page");
      setError("You do not have permission to add patients.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Navigating to Add Patients page");
    toggleSidebar();
    navigate("/patients/add-patients");
  };

  const handleUpdatePurchaseClick = (patient) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Patients page");
      setError("You do not have permission to edit patients.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Navigating to Update Patients page for patient:",
      patient.patient_id
    );
    toggleSidebar();
    navigate("/patients/update-patients", { state: { patient } });
  };

  const handleViewClick = (patient) => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Patients page");
      setError("You do not have permission to view patients.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Navigating to View Patient page for patient:",
      patient.patient_id
    );
    toggleSidebar();
    navigate(`/patients/view/${patient.patient_id}`, { state: { patient } });
  };

  const handleViewHistory = (patient) => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Patients page");
      setError("You do not have permission to view patient history.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Navigating to Patient History page for patient:",
      patient.patient_id
    );
    navigate(`/patients/history/${patient.patient_id}`);
  };

  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Patients page");
      setError("You do not have permission to delete patients.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid patient ID:", id);
      setError("Invalid patient ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Patient modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    console.log("Closing Delete Patient modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Patients page");
      setError("You do not have permission to delete patients.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid patient ID:", deleteId);
      setError("Invalid patient ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting patient with ID:", deleteId);
      await axios.delete(`${Apiurl}/patient_details/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting patient:",
        error.message,
        error.response?.data
      );
      setError("Failed to delete patient. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (loadingPrivileges) {
    return <div className="text-center py-5">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Patients page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
      `}</style>

      {hasPermission("create") && (
        <div className="text-end">
          <button
            className="btn btn-custom btn-sm"
            onClick={handleAddPurchaseClick}
          >
            <i className="fas fa-plus me-2"></i> Add Patients
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="bg-white rounded shadow-sm">
            <div className="row mb-3 crm-table-toolbar">
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-table"></i>
                  </span>
                  <select
                    className="form-select"
                    value={showEntries}
                    onChange={(e) => {
                      console.log(
                        "Changing items per page to:",
                        e.target.value
                      );
                      setShowEntries(e.target.value);
                    }}
                  >
                    <option value="All">Show All</option>
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-7"></div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-5"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => {
                        console.log("Search term changed to:", e.target.value);
                        setSearchTerm(e.target.value);
                      }}
                      style={{ width: "250px", paddingRight: "2.5rem" }}
                    />
                    <Search
                      size={16}
                      className="position-absolute text-muted"
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                  <tr>
                    <th>S.No</th>
                    <th>HIS No</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Gender</th>
                    <th>Age</th>
                    <th>Contact</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr key={item._id} className="align-middle">
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{item.patient_id || "-"}</td>
                        <td>{item.first_name || "-"}</td>
                        <td>{item.patient_type || "-"}</td>
                        <td>{item.gender || "-"}</td>
                        <td>{item.age || "-"}</td>
                        <td>{item.mobile_no || "-"}</td>
                        <td>
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Delete"
                              onClick={() => openDeleteModal(item._id)}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Edit"
                              onClick={() => handleUpdatePurchaseClick(item)}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                          {hasPermission("read") && (
                            <>
                              <button
                                className="btn btn-sm p-1 border-0"
                                title="View"
                                onClick={() => handleViewClick(item)}
                              >
                                <Eye size={16} className="text-secondary" />
                              </button>
                              <button
                                className="btn btn-sm p-1 border-0"
                                title="View History"
                                onClick={() => handleViewHistory(item)}
                              >
                                <Info size={16} className="text-warning" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        {loading ? (
                          "Loading..."
                        ) : (
                          <div className="text-muted">
                            <i className="fas fa-search fa-2x mb-2"></i>
                            <br />
                            No patients found matching your search criteria
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage > 1 && handlePageClick(currentPage - 1)
                  }
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  <span className="d-none d-sm-inline">Previous</span>
                </button>

                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === currentPage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === currentPage
                          ? { backgroundColor: "#20c997", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage < totalPages && handlePageClick(currentPage + 1)
                  }
                  disabled={currentPage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="deletePatientModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deletePatientModalLabel">
                  Delete Patient
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this patient?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
}

export default Patients;
